import React from 'react'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import styled, { css } from 'styled-components'
import PixelIcon from '../pixel-button-icon/pixel-icon'
import { PixelFactoryContext } from '../pixel-factory/pixel-factory'
import PixelFlexBox from '../pixel-flex-box/pixel-flex-box'
import { PixelText } from '../pixel-text/pixel-text'
import { $DCprimaryColor, colors } from '../styleGuide'

export interface PixelBannerProps {
  label?: any
  type?: 'info' | 'warning' | 'error' | 'primary'
  isDismissible?: boolean
  icon?: string | any
  dismissAfter?: number
  onDismiss?: () => void
  isShowBanner?: boolean
}

const StyledPixelBanner = styled.div<{
  isShow: boolean
  bgColor: string
  color: string
  type: string
}>`
  display: ${(props) => (props.isShow ? 'flex' : 'none')};
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 50px;
  padding: 0 10px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 230;
  background-color: ${(props) => props.bgColor};
`

export const PixelBanner = React.forwardRef<
  HTMLButtonElement,
  PixelBannerProps
>((props, ref) => {
  const {
    label = 'Pixel Banner',
    type,
    isDismissible = false,
    icon,
    dismissAfter,
    onDismiss,
    isShowBanner,
    ...rest
  } = props

  // Safe context access with null check
  const contextValue = React.useContext(PixelFactoryContext)
  const bannerInfo = contextValue?.bannerInfo

  const [properties, setProperties] = React.useState<PixelBannerProps>({})
  const [isShow, setIsShow] = React.useState(true)
  const [colorScheme, setColorScheme] = React.useState({
    color: '',
    bgColor: '',
  })

  React.useEffect(() => {
    const customProperties = {
      label: label,
      type: type,
      isDismissible: isDismissible,
      icon: icon,
      dismissAfter: dismissAfter,
      onDismiss: onDismiss,
      isShowBanner: isShowBanner,
    }
    setProperties(customProperties)
  }, [label, type, isDismissible, icon, dismissAfter, onDismiss, isShowBanner])

  React.useEffect(() => {
    switch (properties.type) {
      case 'primary':
        setColorScheme({
          color: colors.text,
          bgColor: $DCprimaryColor,
        })
        break
      case 'warning':
        setColorScheme({
          color: colors.warning,
          bgColor: colors.warningLight,
        })
        break
      case 'info':
        setColorScheme({
          color: colors.info,
          bgColor: colors.infoLight,
        })
        break
      case 'error':
        setColorScheme({
          color: colors.danger,
          bgColor: colors.dangerLight,
        })
        break
      default:
        setColorScheme({
          color: colorScheme.color,
          bgColor: colorScheme.bgColor,
        })
        break
    }
  }, [properties.type])

  React.useEffect(() => {
    // Only proceed if bannerInfo exists and context is available
    if (bannerInfo && bannerInfo !== properties) {
      setProperties({
        label: bannerInfo.label,
        type: bannerInfo.type,
        isDismissible: bannerInfo.isDismissible,
        icon: bannerInfo.icon,
        dismissAfter: bannerInfo.dismissAfter,
        onDismiss: bannerInfo.onDismiss,
        isShowBanner: bannerInfo.isShowBanner,
      })
      setIsShow(!!bannerInfo.isShowBanner)
    } else if (!contextValue) {
      // If no context, hide the banner
      setIsShow(false)
    }
  }, [bannerInfo, contextValue])

  React.useEffect(() => {
    if (properties.dismissAfter && properties.dismissAfter > 0) {
      const timer = setTimeout(() => {
        setIsShow(false)
        properties.onDismiss && properties.onDismiss()
      }, properties.dismissAfter)

      // Cleanup timeout on unmount
      return () => clearTimeout(timer)
    }
  }, [properties.dismissAfter, properties.onDismiss])

  return (
    <React.Fragment>
      <StyledPixelBanner
        color={colorScheme.color}
        bgColor={colorScheme.bgColor}
        isShow={isShow}
        type={properties.type || 'info'}
      >
        <PixelFlexBox
          alignContent="center"
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <PixelIcon
            padding="5px 0px 0px 0px"
            icon={properties.icon}
            color={colorScheme.color}
          />{' '}
          &nbsp;
          <PixelText multiLine customColor={colorScheme.color}>
            {properties.label}
          </PixelText>
        </PixelFlexBox>
        {properties.isDismissible && (
          <PixelIcon
            icon={faXmark}
            fontSize={'20px'}
            color={colorScheme.color}
            onClick={() => setIsShow(false)}
          />
        )}
      </StyledPixelBanner>
    </React.Fragment>
  )
})

export default PixelBanner
